import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronRight, Flame, Salad, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { cn } from "../../lib/utils";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import GlucoseCurveComparisonPanel from "./GlucoseCurveComparisonPanel";
import PoweringPredictionCard from "./PoweringPredictionCard";
import homeService, {
  type HomePageData,
  type InterventionResponse,
} from "../../services/HomeService";
import { useEnsureSessionReady } from "../../hooks/useEnsureSessionReady";
import { validateMealId } from "../../utils/homePageUtils";
import { buildInterventionChartData } from "../../utils/buildInterventionChartData";
import { resolvePlateOptimisationData } from "../../utils/plateOptimisationStorage";
import {
  capitalizeActivityLabel,
  formatEatingWindowBadge,
  formatMealElapsedLong,
} from "../../utils/idleHomeFormatters";
import { resolveMealDisplayName } from "../../utils/resolveMealDisplayName";
import HealthInsightsHomeCard from "./HealthInsightsHomeCard";
import HomeBbsPillarsSummary from "./HomeBbsPillarsSummary";
import MealBbsScoreRow from "./MealBbsScoreRow";

interface LatestMealSummary {
  meal_id?: string;
  meal_type?: string;
  meal_time?: string;
  title?: string;
}

interface GlucoseSpikeSummary {
  predicted_spike_mg_dl?: number;
  this_meal_spike_mg_dl?: number;
  baseline_glucose?: number;
  glucose_curve?: {
    points: Array<{ time_min: number; glucose_mg_dl: number }>;
  };
}

export interface IdleHomeSectionProps {
  homeData?: HomePageData;
  userName?: string;
  latestMeal?: LatestMealSummary;
  mealId?: string;
  glucoseSpikeData?: GlucoseSpikeSummary;
  selectedDate?: string;
  showScorePillars?: boolean;
}

const MEALS_TARGET = 3;

const IdleHomeSection = ({
  homeData,
  userName: _userName = "there",
  latestMeal,
  mealId,
  glucoseSpikeData,
  selectedDate,
  showScorePillars,
}: IdleHomeSectionProps) => {
  const navigate = useNavigate();
  const { canUseAuthenticatedApis } = useEnsureSessionReady();
  const validMealId = validateMealId(mealId ?? null);

  const { data: apiData } = useQuery<InterventionResponse | null>({
    queryKey: ["idle-last-meal-intervention", validMealId],
    queryFn: () => homeService.calculateIntervention(validMealId!, "walking"),
    enabled: canUseAuthenticatedApis && Boolean(validMealId),
    staleTime: 5 * 60 * 1000,
  });

  const curvePoints = glucoseSpikeData?.glucose_curve?.points ?? [];
  const fbBaseline =
    glucoseSpikeData?.baseline_glucose ??
    curvePoints[0]?.glucose_mg_dl ??
    apiData?.now_glucose ??
    100;
  const fbSpike =
    glucoseSpikeData?.this_meal_spike_mg_dl ??
    glucoseSpikeData?.predicted_spike_mg_dl ??
    68;

  const noActionPeak =
    apiData?.original_peak ??
    curvePoints.reduce(
      (max, p) => (p.glucose_mg_dl > max ? p.glucose_mg_dl : max),
      fbBaseline + fbSpike
    );

  const withActionPeak =
    apiData?.new_peak ??
    Math.max(
      noActionPeak - (apiData?.intervention_drop_mg_dl ?? 26),
      fbBaseline
    );

  const dropMgDl = Math.round(
    apiData?.intervention_drop_mg_dl ?? noActionPeak - withActionPeak
  );

  const activityLabel =
    apiData?.activity?.replace(/_/g, " ") ?? "walking";
  const activityDisplay = capitalizeActivityLabel(activityLabel);

  const chartData = useMemo(
    () => buildInterventionChartData(apiData, curvePoints, dropMgDl),
    [apiData, curvePoints, dropMgDl]
  );

  const mealsLogged =
    homeData?.["Food GHI"]?.meals_logged ??
    homeData?.["Bite Balance Score"]?.meals_logged ??
    homeData?.["Glucose Spikes"]?.daily_summary?.meals_with_spike_saved_count ??
    0;

  const fasting = homeData?.["Intermittent Fasting"];
  const eatingBadge = formatEatingWindowBadge(
    fasting?.["Time Remaining UI"],
    fasting?.["Time Remaining"]
  );

  const streakDays = homeData?.["Streak"]?.current_days ?? homeData?.group_streak?.days ?? 0;
  const streakLabel =
    streakDays > 0 ? `DAY ${streakDays} STREAK` : homeData?.group_streak?.label?.toUpperCase() ?? "DAY STREAK";

  const interventionDropMgDl = apiData?.intervention_drop_mg_dl;
  const interventionDropRounded =
    interventionDropMgDl != null && !Number.isNaN(interventionDropMgDl)
      ? Math.round(interventionDropMgDl)
      : null;

  const mealTitle = resolveMealDisplayName({
    mealName: apiData?.meal_name ?? latestMeal?.meal_name,
    mealTitle: latestMeal?.title,
    productName: (latestMeal as { product_name?: string } | undefined)?.product_name,
    mealType: latestMeal?.meal_type,
    mealItems: apiData?.meal_items,
    fallback: "Your last meal",
  });

  const bent =
    (interventionDropRounded != null && interventionDropRounded > 0) ||
    dropMgDl > 0 ||
    withActionPeak < noActionPeak - 1;

  const plateData = resolvePlateOptimisationData({
    homePlate: homeData?.plate_optimisation,
  });

  const insightDrop =
    interventionDropRounded ?? (dropMgDl > 0 ? dropMgDl : 0);

  const insightText = useMemo(() => {
    if (plateData?.subtext?.trim()) return plateData.subtext;
    const stepLabels = plateData?.steps?.map((s) => s.label).filter(Boolean);
    if (stepLabels?.length) {
      return `The ${activityDisplay} worked. ${stepLabels.join(" + ")} together bent your peak by ${insightDrop} mg/dL.`;
    }
    return `The ${activityDisplay} worked. Eating salad first + 15-min ${activityDisplay} together bent your peak by ${insightDrop} mg/dL.`;
  }, [plateData, activityDisplay, insightDrop]);

  const elapsedLabel = formatMealElapsedLong(latestMeal?.meal_time);

  const mealBbs = useMemo(() => {
    const apiScore = apiData?.bbs_score;
    const apiLabel = apiData?.bbs_label ?? "";
    if (apiScore == null) return null;

    const score = Math.round(apiScore);
    const lower = apiLabel.toLowerCase();
    const isGood = lower.includes("excellent") || lower.includes("good");
    const isMid =
      lower.includes("caution") ||
      lower.includes("fair") ||
      lower.includes("moderate") ||
      lower.includes("improvement");

    return { score, label: apiLabel, isGood, isMid };
  }, [apiData?.bbs_score, apiData?.bbs_label]);

  // Groups — all data comes from homeData["Family Health"] (food-home-page API)
  const homeGroup = homeData?.["Family Health"]?.groups?.[0];
  const groupName = homeGroup?.name ?? "Your family";
  const groupMembersTotal =
    homeGroup?.summary?.members_total ?? homeGroup?.members?.length ?? 0;
  const groupMembersLogged =
    homeGroup?.members?.filter((m) => m.meals_logged > 0).length ?? 0;
  // Use group name initials (not member initial) for the group avatar
  const groupInitial = groupName.slice(0, 2).toUpperCase();
  const groupStreakDays = homeData?.group_streak?.days ?? 0;
  const groupLoggedToday = homeData?.group_streak?.all_members_logged_today;
  const groupRank = homeData?.group_streak?.rank;
  const groupTotalGroups = homeData?.group_streak?.total_groups;

  return (
    <div className="space-y-4 pb-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-2xs font-bold uppercase tracking-[0.08em] text-white shadow-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
          {eatingBadge}
        </span>
        <span className="inline-flex rounded-full border border-border/80 bg-card px-3 py-1.5 text-2xs font-bold uppercase tracking-[0.08em] text-foreground">
          {streakLabel}
        </span>
        <span className="text-compact-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
          {mealsLogged} of {MEALS_TARGET} logged
        </span>
      </div>

      {mealId ? (
        <Card className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-md">
          <CardContent className="space-y-0 p-0">
            <div className="flex items-center justify-between gap-2 border-b border-border/50 px-4 py-3">
              <span className="text-compact-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Last meal
              </span>
              <div className="flex items-center gap-2">
                {bent ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-2xs font-bold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-800/50">
                    <Check className="h-3 w-3 shrink-0" strokeWidth={3} />
                    Bent
                  </span>
                ) : null}
                {elapsedLabel ? (
                  <span className="text-2xs font-bold uppercase tracking-wide text-muted-foreground">
                    {elapsedLabel}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="space-y-3 px-4 py-4">
              <div className="flex gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-100 dark:bg-emerald-950/30 dark:ring-emerald-900/40">
                  <Salad className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-snug text-foreground">
                    {mealTitle}
                  </p>
                  {(apiData?.meal_totals?.calorie_content_kcal || apiData?.meal_totals?.carbohydrate_content_g || apiData?.meal_totals?.GI_estimate) && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {apiData.meal_totals.calorie_content_kcal && apiData.meal_totals.calorie_content_kcal > 0 && (
                        <span className="font-medium">
                          <span className="font-semibold text-foreground">{Math.round(apiData.meal_totals.calorie_content_kcal)}</span> kcal
                        </span>
                      )}
                      {apiData.meal_totals.carbohydrate_content_g && apiData.meal_totals.carbohydrate_content_g > 0 && (
                        <span className="font-medium">
                          <span className="font-semibold text-foreground">{Math.round(apiData.meal_totals.carbohydrate_content_g)}</span> g carbs
                        </span>
                      )}
                      {apiData.meal_totals.GI_estimate && apiData.meal_totals.GI_estimate > 0 && (
                        <span className="font-medium">
                          GI <span className="font-semibold text-foreground">{Math.round(apiData.meal_totals.GI_estimate)}</span>
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="text-compact-xs font-bold uppercase tracking-wide text-muted-foreground">
                      Peak
                    </span>
                    <span className="text-sm font-black tabular-nums text-foreground">
                      {Math.round(noActionPeak)}
                      <span className="mx-1 font-normal text-muted-foreground">
                        →
                      </span>
                      {Math.round(withActionPeak)}
                      <span className="ml-1 text-compact-xs font-bold text-muted-foreground">
                        mg/dL
                      </span>
                    </span>
                    {interventionDropRounded != null && interventionDropRounded > 0 ? (
                      <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-emerald-600 px-1.5 text-compact-xs font-bold text-white">
                        {interventionDropRounded}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>

              {mealBbs ? (
                <MealBbsScoreRow score={mealBbs.score} label={mealBbs.label} />
              ) : null}

              <GlucoseCurveComparisonPanel
                chartData={chartData}
                noActionPeak={noActionPeak}
                withActionPeak={withActionPeak}
                dropMgDl={dropMgDl}
                activityLabel={activityDisplay}
                nowGl={apiData?.now_glucose ?? curvePoints[0]?.glucose_mg_dl}
                chartHeightClass="h-[7.5rem]"
                showPeakComparison={false}
                withActionStroke="#22c55e"
                idleChartMode
                compactLegend
              />
            </div>

            {bent && insightDrop > 0 ? (
              <div className="flex items-start gap-2.5 border-t border-emerald-100/80 bg-emerald-50/70 px-4 py-3 dark:border-emerald-900/30 dark:bg-emerald-950/25">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <p className="text-xs leading-relaxed text-emerald-900 dark:text-emerald-100/90">
                  {insightText}
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Streak + meals logged */}
      <div className="grid grid-cols-2 gap-2">
        <Card className="rounded-xl border border-border/70 bg-card px-2 py-3 text-center shadow-sm">
          <p className="text-2xs font-bold uppercase tracking-wide text-muted-foreground">
            Meals today
          </p>
          <p className="mt-1 text-lg font-black tabular-nums text-foreground">
            {homeData?.["Bite Balance Score"]?.meals_logged ??
              homeData?.["Glucose Spikes"]?.total_meals_count ??
              homeData?.["Glucose Spikes"]?.meals?.length ??
              0}
          </p>
          <p className="text-2xs font-semibold text-muted-foreground">logged</p>
        </Card>
        <Card className="rounded-xl border border-border/70 bg-card px-2 py-3 text-center shadow-sm">
          <p className="text-2xs font-bold uppercase tracking-wide text-muted-foreground">
            Streak
          </p>
          <p className="mt-1 text-lg font-black tabular-nums text-foreground">
            {streakDays > 0 ? streakDays : "—"}
          </p>
          <p className="text-2xs font-semibold text-muted-foreground">days</p>
        </Card>
      </div>

      {homeData?.["Health Insights"] ? (
        <HealthInsightsHomeCard healthInsights={homeData["Health Insights"]} />
      ) : null}

      {(homeData?.["Powering This Prediction"] ?? []).length > 0 ? (
        <PoweringPredictionCard items={homeData?.["Powering This Prediction"]} />
      ) : null}

      <HomeBbsPillarsSummary
        homeData={homeData}
        selectedDate={selectedDate}
        showFourPillars={showScorePillars}
      />

      {/* Plan ahead card — removed */}

      {/* Family group streak card — TEMPORARILY COMMENTED OUT (waiting for rank/total_groups from backend)
      <Card className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <CardContent className="flex items-center gap-3 px-4 py-3.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:ring-amber-800/50">
            <span className="text-compact-sm font-bold uppercase text-amber-700 dark:text-amber-300">
              {groupInitial}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">
              {groupName}
              {groupStreakDays > 0 && (
                <span className="text-amber-600 dark:text-amber-400">
                  {" · "}{groupStreakDays}-day group streak
                </span>
              )}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-compact-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
              {groupMembersTotal > 0 ? (
                <span>{groupMembersLogged} of {groupMembersTotal} logged today</span>
              ) : (
                <span>Group active</span>
              )}
              {groupRank != null && groupTotalGroups != null && (
                <>
                  <span className="text-border/60">·</span>
                  <span>Rank {groupRank} of {groupTotalGroups} groups</span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {groupStreakDays > 0 && <Flame className="h-4 w-4 text-amber-500" />}
            {groupLoggedToday && (
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      */}
    </div>
  );
};

export default IdleHomeSection;
